import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';

import { Table } from '../../shared/table/table';
import { TableAction, TableColumn } from '../../shared/models/table-models';
import { Popup } from '../../../core/shared/components/popup/popup';
import { faEye, faPencil } from '@fortawesome/free-solid-svg-icons';
import { DynamicFieldConfig } from '../../../core/shared/dynamic-form/form-config.model';
import { ToastService } from '../../../core/shared/services/toast.service';
import { Setup } from '../shared/service/setup';
import { CurrencyPayload } from '../shared/models/guarantor.model';

const STATUS_ACTIVE = 706;
const STATUS_INACTIVE = 707;

/** Currency whose name does not open the rate screen. */
const NO_RATE_CRNCY_SEQ = 2;

interface UsdRate {
  srNo: number;
  crncySeq: number | null;
  crncyNm: string;
  stsKey: number;
}

@Component({
  selector: 'app-usd-rate-list',
  imports: [TranslatePipe, Table, Popup],
  templateUrl: './usd-rate-list.html',
  styleUrl: './usd-rate-list.css',
})
export class UsdRateList implements OnInit {
  showPopup = false;
  faPencil = faPencil;
  faEye = faEye;
  popupTitle = '';
  isViewMode = false;

  constructor(
    private setupService: Setup,
    private toast: ToastService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.loadRates();
  }

  popupFields: DynamicFieldConfig[] = [];

  // ================= TABLE CONFIG =================
  columns: TableColumn[] = [
    { key: 'srNo', label: 'SETUP.USD_RATE.SR_NO', type: 'text', width: '20%' },
    {
      key: 'crncyNm',
      label: 'SETUP.USD_RATE.CURRENCY_NAME',
      type: 'link',
      width: '40%',
      // PKR (crncySeq 2) has no rate screen, so its name is not a link.
      linkDisabled: (row) => Number(row.crncySeq) === NO_RATE_CRNCY_SEQ,
      onClick: (row) =>
        this.router.navigate(['/currency-rate'], {
          queryParams: { crncySeq: row.crncySeq, crncyNm: row.crncyNm }
        })
    },
    {
      key: 'stsKey',
      label: 'COMMON.STATUS',
      type: 'badge',
      width: '24%',
      map: { [STATUS_ACTIVE]: 'COMMON.ACTIVE', [STATUS_INACTIVE]: 'COMMON.INACTIVE' },
      badgeMap: { [STATUS_ACTIVE]: 'status-active', [STATUS_INACTIVE]: 'status-inactive' }
    }
  ];

  rates: UsdRate[] = [];
  loading = false;

  loadRates() {
    this.loading = true;
    this.setupService.getCurrencyList().subscribe({
      next: (res: any) => {
        this.loading = false;
        const list: any[] = Array.isArray(res) ? res : res?.data || [];

        this.rates = list
          .filter((item) => Number(item?.delFlg ?? 0) !== 1)
          .map((item, index) => ({
            srNo: index + 1,
            crncySeq: item.crncySeq ?? null,
            crncyNm: item.crncyNm ?? '',
            stsKey: Number(item.stsKey ?? STATUS_ACTIVE)
          }));
      },
      error: (err) => {
        this.loading = false;
        console.error('Failed to load USD rates', err);
      }
    });
  }

  selectedRow: UsdRate | null = null;

  private buildFields(row: UsdRate | null, required: boolean): DynamicFieldConfig[] {
    return [
      {
        label: 'SETUP.USD_RATE.CURRENCY_NAME',
        key: 'crncyNm',
        type: 'text',
        value: row?.crncyNm ?? '',
        validations: required ? { required: true } : undefined
      },
      {
        label: 'COMMON.STATUS',
        key: 'stsKey',
        type: 'select',
        value: row?.stsKey ?? STATUS_ACTIVE,
        options$: [
          { label: 'COMMON.ACTIVE', value: STATUS_ACTIVE },
          { label: 'COMMON.INACTIVE', value: STATUS_INACTIVE }
        ],
        validations: required ? { required: true } : undefined
      }
    ];
  }

  openUsdRate() {
    this.selectedRow = null;
    this.popupTitle = 'SETUP.USD_RATE.ADD_USD_RATE';
    this.isViewMode = false;
    this.popupFields = this.buildFields(null, true);
    this.showPopup = true;
  }

  // ================= TABLE ACTIONS =================
  actions: TableAction[] = [
    {
      icon: this.faPencil,
      class: 'text-indigo-600',
      tooltip: 'COMMON.EDIT',
      onClick: (row: any) => {
        this.selectedRow = row;
        this.popupTitle = 'SETUP.USD_RATE.EDIT_USD_RATE';
        this.isViewMode = false;
        this.popupFields = this.buildFields(row, true);
        this.showPopup = true;
      }
    },
    {
      icon: this.faEye,
      class: 'text-green-600',
      tooltip: 'COMMON.VIEW',
      onClick: (row: any) => {
        this.selectedRow = row;
        this.popupTitle = 'SETUP.USD_RATE.VIEW_USD_RATE';
        this.isViewMode = true;
        this.popupFields = this.buildFields(row, false);
        this.showPopup = true;
      }
    }
  ];

  /** Returns false so the popup only closes once the API succeeds. */
  SaveUsdRate(formData: any): boolean {
    const payload = this.createPayload(formData);

    this.setupService.saveCurrency(payload).subscribe({
      next: () => {
        this.toast.success('SETUP.SAVED_SUCCESSFULLY');
        this.showPopup = false;
        this.loadRates();
      },
      error: (err) => {
        console.error('Save failed', err);
        this.toast.error('SETUP.FAILED_TO_SAVE_RECORD');
      }
    });

    return false;
  }

  private createPayload(formData: any): CurrencyPayload {
    return {
      crncySeq: this.selectedRow?.crncySeq ?? null,
      crncyNm: String(formData.crncyNm ?? '').trim(),
      stsKey: Number(formData.stsKey ?? STATUS_ACTIVE),
      delFlg: 0
    };
  }
}
